import groq from "groq"

import { groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"
import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { groqBranchOfficeProjection } from "../../shared/sanity/branchOffice.projection"
import { getStockKeys } from "../../shared/utils/getVariantListing"

export const queryProductStocks = {
  async productStocks(_parent, args, context, _info) {
    const { sku, cdn, lang, offset, limit } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.PRODUCT_STOCK,
      operation: Operations.READ,
    })

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const commerceApi = CommerceService.api()

    const { draftsFilter, pipesAndLimits } = groqQueryOptions({
      offset,
      limit,
      includeDrafts: true,
    })

    const branchOffices = await client.fetch(
      groq`*[_type=="branchOffice" ${draftsFilter}]${pipesAndLimits} {
        ...,
        ${groqBranchOfficeProjection()}
      }`,
    )

    if (!branchOffices || branchOffices?.items?.length <= 0) return null

    const stockKeysWithSkus = await getStockKeys({ skus: [sku] })

    const stockKeys = stockKeysWithSkus.map((stockKey) => `"${stockKey?.stockKey}"`).join(", ")

    const listings = (
      await commerceApi
        .inventory()
        .get({
          queryArgs: {
            limit,
            expand: "supplyChannel",
            where: `sku=${stockKeys}`,
          },
        })
        .execute()
    )?.body.results

    const stocksInBranches = branchOffices.map((office) => {
      if (!office) {
        return {
          branchId: null,
          branchName: null,
          availableStocks: null,
          quantityOnStock: null,
          orderedQuantity: null,
          sku,
        }
      }

      let branchData = {
        branchId: office.id,
        branchName: office.title,
        availableStocks: null,
        quantityOnStock: null,
        orderedQuantity: null,
        sku,
      }

      if (!listings || listings.length <= 0) {
        return branchData
      }

      const branchListing = listings.find((listing) => listing.supplyChannel.obj.key === `NL-${office.id}`)

      const customStockFields = branchListing?.custom?.fields

      if (!customStockFields) return branchData

      if (customStockFields?.adm_availableQuantity) {
        branchData.availableStocks = customStockFields?.adm_availableQuantity
      }

      if (customStockFields?.adm_quantityOnStock) {
        branchData.quantityOnStock = customStockFields?.adm_quantityOnStock
      }

      if (customStockFields?.adm_reservedQuantity) {
        branchData.reservedStock = customStockFields?.adm_reservedQuantity
      }

      if (customStockFields?.adm_orderedQuantity) {
        branchData.orderedQuantity = customStockFields?.adm_orderedQuantity
      }

      return branchData
    })

    return {
      items: stocksInBranches,
      total: stocksInBranches.length,
    }
  },
}
